import type { Goals, Metric, Player, PlayerStats, Serve } from "../types";

export const defaultGoals: Goals = {
  avgSpeedFloat: 70,
  avgSpeedHybrid: 75,
  avgSpeedSpin: 100,
  effiencyFloat: 55,
  effiencyHybrid: 65,
  effiencySpin: 75
};

const serveTypes = ["float", "hyb", "spin"];

export function speedForMetric(speed: number, metric: Metric) {
  return metric === "mph" ? speed * 0.621371 : speed;
}

export function formatSpeed(speed: number, metric: Metric) {
  return `${speedForMetric(speed, metric).toFixed(speed ? 1 : 0)} ${metric}`;
}

function average(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function efficiency(aces: number, misses: number, total: number) {
  if (!total) return 0;
  return ((aces - misses) / total) * 100;
}

export function summarizePlayer(player: Player, playerServes: Serve[]): PlayerStats {
  const aces = playerServes.filter((serve) => serve.state === 3);
  const passes = playerServes.filter((serve) => serve.state === 2);
  const misses = playerServes.filter((serve) => serve.state === 1);
  const speeds = playerServes.map((serve) => serve.speed).filter(Number.isFinite);

  const byType = Object.fromEntries(
    serveTypes.map((type) => {
      const typeServes = playerServes.filter((serve) => serve.type === type);
      const typeAces = typeServes.filter((serve) => serve.state === 3).length;
      const typePasses = typeServes.filter((serve) => serve.state === 2).length;
      const typeMisses = typeServes.filter((serve) => serve.state === 1).length;
      const typeSpeeds = typeServes.map((serve) => serve.speed).filter(Number.isFinite);
      return [type, {
        total: typeServes.length,
        aces: typeAces,
        passes: typePasses,
        misses: typeMisses,
        efficiency: efficiency(typeAces, typeMisses, typeServes.length),
        avgSpeed: average(typeSpeeds),
        maxSpeed: typeSpeeds.length ? Math.max(...typeSpeeds) : 0
      }];
    })
  ) as PlayerStats["byType"];

  return {
    ...player,
    name: `${player.firstName} ${player.lastName}`.trim(),
    serves: playerServes,
    totalServes: playerServes.length,
    aces: aces.length,
    passes: passes.length,
    misses: misses.length,
    efficiency: efficiency(aces.length, misses.length, playerServes.length),
    avgSpeed: average(speeds),
    maxSpeed: speeds.length ? Math.max(...speeds) : 0,
    serveSpeedsDict: {
      aces: aces.map((serve) => serve.speed),
      pass: passes.map((serve) => serve.speed),
      misses: misses.map((serve) => serve.speed)
    },
    byType
  };
}

export function makePlayerStats(players: Player[], serves: Serve[], dateRange: { start?: string; end?: string }): PlayerStats[] {
  const start = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
  const end = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;

  return players
    .map((player) => {
      const playerServes = serves.filter((serve) => {
        if (serve.playerId !== player.id) return false;
        if (start && serve.date && serve.date < start) return false;
        if (end && serve.date && serve.date > end) return false;
        return true;
      });
      return summarizePlayer(player, playerServes);
    })
    .sort((a, b) => b.efficiency - a.efficiency);
}

export function aggregateStats(stats: PlayerStats[]): PlayerStats {
  const serves = stats.flatMap((stat) => stat.serves);
  const allSpeeds = serves.map((serve) => serve.speed).filter(Number.isFinite);
  const aces = serves.filter((serve) => serve.state === 3);
  const passes = serves.filter((serve) => serve.state === 2);
  const misses = serves.filter((serve) => serve.state === 1);

  const byType = Object.fromEntries(
    serveTypes.map((type) => {
      const typeServes = serves.filter((serve) => serve.type === type);
      const typeSpeeds = typeServes.map((serve) => serve.speed).filter(Number.isFinite);
      const typeAces = typeServes.filter((serve) => serve.state === 3).length;
      const typePasses = typeServes.filter((serve) => serve.state === 2).length;
      const typeMisses = typeServes.filter((serve) => serve.state === 1).length;
      return [type, {
        total: typeServes.length,
        aces: typeAces,
        passes: typePasses,
        misses: typeMisses,
        efficiency: efficiency(typeAces, typeMisses, typeServes.length),
        avgSpeed: average(typeSpeeds),
        maxSpeed: typeSpeeds.length ? Math.max(...typeSpeeds) : 0
      }];
    })
  ) as PlayerStats["byType"];

  return {
    id: "all",
    firstName: "All",
    lastName: "players",
    name: "All players",
    number: 0,
    position: "Team",
    image: "",
    serves,
    totalServes: serves.length,
    aces: aces.length,
    passes: passes.length,
    misses: misses.length,
    efficiency: efficiency(aces.length, misses.length, serves.length),
    avgSpeed: average(allSpeeds),
    maxSpeed: allSpeeds.length ? Math.max(...allSpeeds) : 0,
    serveSpeedsDict: {
      aces: aces.map((serve) => serve.speed),
      pass: passes.map((serve) => serve.speed),
      misses: misses.map((serve) => serve.speed)
    },
    byType
  };
}

export function passProbabilityBySpeed(stat: PlayerStats, metric: Metric = "km/h") {
  const binSize = metric === "mph" ? 5 : 10;
  const convertedSpeeds = stat.serves.map((serve) => ({ serve, speed: speedForMetric(serve.speed, metric) }));
  const maxSpeed = convertedSpeeds.length ? Math.max(...convertedSpeeds.map((entry) => entry.speed)) : 0;
  const binCount = Math.max(1, Math.ceil(maxSpeed / binSize));

  return Array.from({ length: binCount }, (_, index) => {
    const lower = index * binSize;
    const upper = lower + binSize;
    const serves = convertedSpeeds.filter((entry) => entry.speed >= lower && entry.speed < upper).map((entry) => entry.serve);
    const successful = serves.filter((serve) => serve.state === 2 || serve.state === 3).length;
    return {
      range: `${lower}-${upper}`,
      speed: lower,
      probability: serves.length ? (successful / serves.length) * 100 : 0,
      serves: serves.length
    };
  }).filter((point) => point.serves > 0);
}

export function quantiles(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const pick = (q: number) => {
    const index = (sorted.length - 1) * q;
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
  };
  return {
    min: sorted[0],
    q1: pick(0.25),
    median: pick(0.5),
    q3: pick(0.75),
    max: sorted[sorted.length - 1]
  };
}

export function serveTypeLabel(type: string) {
  if (type === "hyb") return "Hybrid";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function serveTypeKey(label: string) {
  const lower = label.toLowerCase();
  if (lower === "hybrid" || lower === "hybride") return "hyb";
  if (lower === "float") return "float";
  if (lower === "spin") return "spin";
  return lower;
}

export function outcomeState(label: string) {
  if (label === "Ace") return 3;
  if (label === "Pass") return 2;
  if (label === "Miss") return 1;
  return 0;
}

export function previousDateRange(dateRange: { start?: string; end?: string }, serves: Serve[]) {
  const datedServes = serves.filter((serve) => serve.date).sort((a, b) => a.date!.getTime() - b.date!.getTime());
  const latest = datedServes[datedServes.length - 1]?.date ?? new Date();

  if (dateRange.start && dateRange.end) {
    const start = new Date(`${dateRange.start}T00:00:00`);
    const end = new Date(`${dateRange.end}T23:59:59`);
    const span = Math.max(1, end.getTime() - start.getTime());
    return {
      start: new Date(start.getTime() - span).toISOString().slice(0, 10),
      end: new Date(start.getTime() - 1).toISOString().slice(0, 10)
    };
  }

  const priorEnd = new Date(latest);
  priorEnd.setDate(priorEnd.getDate() - 30);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - 30);
  return {
    start: priorStart.toISOString().slice(0, 10),
    end: priorEnd.toISOString().slice(0, 10)
  };
}
